import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setVizLoading } from '../../../../store/visualizerSlice';
import ProductGroupStep from '../ProductGroupStep/ProductGroupStep';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './Cabinets.module.css';

// Kitchen arrangements that include a wood hood — the Wood Hood tab in the
// Cabinets step is only visible when the chosen arrangement is one of these.
const WOODHOOD_ARRANGEMENTS = ['C-1', 'L-1', 'Q-1'];
export function hasWoodhood(kitchenArrangement) {
    const s = String(kitchenArrangement || '').toUpperCase();
    return WOODHOOD_ARRANGEMENTS.some(a => s.includes(a));
}

// Each tab targets a specific surface name on the scene (confirmed by client):
//   Perimeter cabinets → "Cabinet Base"
//   Island cabinets    → "Island Cabinet Base"
//   Wood hood          → "Vent Hood"
const TAB_SURFACE_MATCHERS = {
    Cabinets: (name) => name === 'Cabinet Base',
    Island:   (name) => name === 'Island Cabinet Base',
    WoodHood: (name) => name === 'Vent Hood'
};

function applyToMatchingSurfaces(productId, matcher, dispatch) {
    const chamViz = document.getElementById('chamViz');
    if (!chamViz || !productId) return;
    dispatch(setVizLoading(true));
    chamViz.applyByFilter(
        productId,
        '',
        function (newSel) {
            if (matcher(this.name)) return newSel;
            return this.sel;
        },
        true
    );
}

export default function Cabinets({
    stepIndex,
    room = 'Kitchen',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const dispatch = useDispatch();
    const kitchenArrangement = useSelector(
        state => state.visualizer.sceneMeta?.Kitchen?.kitchenArrangement || ''
    );

    // Island + Wood Hood are kitchen-only concepts. Bathrooms just show the
    // plain "Cabinets" tab.
    const isKitchen = String(room).toLowerCase() === 'kitchen';

    // Show the Wood Hood tab only when the arrangement supports it. If no
    // arrangement has been chosen yet, show it too (matches the default C-1).
    const showHood = isKitchen && (!kitchenArrangement || hasWoodhood(kitchenArrangement));

    const TABS = useMemo(() => {
        const base = ['Cabinets'];
        if (isKitchen) base.push('Island');
        if (showHood) base.push('WoodHood');
        return base;
    }, [isKitchen, showHood]);

    const [activeTab, setActiveTab] = useState('Cabinets');

    // If the arrangement changes and the active tab is now hidden, fall back.
    if (!TABS.includes(activeTab)) {
        // Safe to call setState during render for correctness — React will re-render.
        setActiveTab('Cabinets');
    }

    const handleCabinetsSelect = useCallback((product) => {
        applyToMatchingSurfaces(product.id, TAB_SURFACE_MATCHERS.Cabinets, dispatch);
    }, [dispatch]);

    const handleIslandSelect = useCallback((product) => {
        applyToMatchingSurfaces(product.id, TAB_SURFACE_MATCHERS.Island, dispatch);
    }, [dispatch]);

    const handleHoodSelect = useCallback((product) => {
        applyToMatchingSurfaces(product.id, TAB_SURFACE_MATCHERS.WoodHood, dispatch);
    }, [dispatch]);

    const tabLabel = (t) => {
        if (t === 'Island') return 'Island Cabinets';
        if (t === 'WoodHood') return 'Wood Hood';
        return 'Cabinets';
    };

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title="Cabinets"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.tabs}>
                {TABS.map(tab => (
                    <button
                        key={tab}
                        type="button"
                        className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tabLabel(tab)}
                    </button>
                ))}
            </div>

            <div className={styles.tabContent}>
                {activeTab === 'Cabinets' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="Cabinets"
                            seriesId="9785"
                            surfaceName="Cabinet Base"
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            onProductSelect={handleCabinetsSelect}
                        />
                    </div>
                )}

                {activeTab === 'Island' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="IslandCabinets"
                            seriesId="9785"
                            surfaceName="Island Cabinet Base"
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            onProductSelect={handleIslandSelect}
                        />
                    </div>
                )}

                {activeTab === 'WoodHood' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="WoodHood"
                            seriesId="9785"
                            surfaceName="Vent Hood"
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            onProductSelect={handleHoodSelect}
                            forceBaseThumb
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

Cabinets.propTypes = {
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
